import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "container", "errors", "submit", "coverIndex", "addPicturesButton"]
  static values = {
    existingCount: Number,
    hasExistingCover: Boolean,
    coverText: { type: String, default: "Cover" },
    useAsCoverImageText: { type: String, default: "Use as cover image" },
    saveImagesText: { type: String, default: "Save Images" },
    uploadingImagesText: { type: String, default: "Uploading images…" },
    loadingImagesText: { type: String, default: "Loading images…" },
    loadingFileText: { type: String, default: "Loading %{name}…" },
    failedToLoadFileText: { type: String, default: "Failed to load %{name}" },
    fileTooLargeText: { type: String, default: "\"%{name}\" is too large. Each image must be less than 25MB." },
    invalidImageTypeText: { type: String, default: "\"%{name}\" is not a valid image. Only PNG, JPG, JPEG, or WEBP allowed." },
    maxImagesErrorText: { type: String, default: "You already uploaded the maximum of %{count} images." },
    tooManyImagesText: { type: String, default: "You can only add %{count} more image." },
    tooManyImagesPluralText: { type: String, default: "You can only add %{count} more images." },
    maxLimitReachedText: { type: String, default: "You have reached the maximum limit of %{count} images." },
    maxFiles: { type: Number, default: 3 },
  };

  connect() {
    this.files = [] // store selected files with unique IDs
    this.counter = 0 // unique ID counter
    this.pendingReads = 0
    this.activeUploads = 0
    this.selectedCoverFileId = null
    this.updateAddButton()
  }

  showPreviews(event) {
    this.clearErrors()

    const newFiles = Array.from(event.target.files)
    const totalAlready = this.existingCountValue + this.files.length

    if (totalAlready >= this.maxFilesValue) {
      this.addError(this._interpolate(this.maxImagesErrorTextValue, { count: this.maxFilesValue }))
      this.inputTarget.value = ""
      return
    }

    if (totalAlready + newFiles.length > this.maxFilesValue) {
      const allowed = this.maxFilesValue - totalAlready
      const text = allowed === 1 ? this.tooManyImagesTextValue : this.tooManyImagesPluralTextValue
      this.addError(this._interpolate(text, { count: allowed }))
      this.inputTarget.value = ""
      return
    }

    const validTypes = ["image/png", "image/jpeg", "image/jpg", "image/webp"]
    const validSize = 25 * 1024 * 1024 // 25MB
    const validFiles = []

    newFiles.forEach(file => {
      if (validTypes.includes(file.type)) {
        if (file.size > validSize) {
          this.addError(this._interpolate(this.fileTooLargeTextValue, { name: file.name }))
          return
        }
        file._id = this.counter++
        validFiles.push(file)
      } else {
        this.addError(this._interpolate(this.invalidImageTypeTextValue, { name: file.name }))
      }
    })

    this.files.push(...validFiles)

    // Auto-select first file as cover ONLY IF no existing cover and none selected
    if (!this.hasExistingCoverValue && this.selectedCoverFileId === null && this.files.length > 0) {
      this.selectedCoverFileId = this.files[0]._id
    }

    this.renderPreviews()
    this.updateInputFiles()
    this.syncCoverIndex()
  }

  removeFile(fileId) {
    this.files = this.files.filter(f => f._id !== fileId)
    const wrapper = this.containerTarget.querySelector(`[data-file-id="${fileId}"]`)
    if (wrapper) wrapper.remove()

    // If the removed file was the cover, reset to first file
    if (this.selectedCoverFileId === fileId) {
      this.selectedCoverFileId = this.files.length > 0 ? this.files[0]._id : null
      this.updateCoverBadges()
    }

    this.updateInputFiles()
    this.syncCoverIndex()
    this.updateAddButton()
  }

  selectCover(fileId) {
    this.selectedCoverFileId = fileId
    this.updateCoverBadges()
    this.syncCoverIndex()
  }

  syncCoverIndex() {
    if (!this.hasCoverIndexTarget) return
    const index = this.files.findIndex(f => f._id === this.selectedCoverFileId)
    this.coverIndexTarget.value = index >= 0 ? index : -1
  }

  updateCoverBadges() {
    this.containerTarget.querySelectorAll("[data-file-id]").forEach(wrapper => {
      const fid = parseInt(wrapper.dataset.fileId)
      const isCover = fid === this.selectedCoverFileId
      const badge = wrapper.querySelector(".cover-badge")
      const radio = wrapper.querySelector(".cover-radio")

      if (badge) {
        badge.classList.toggle("hidden", !isCover)
      }
      if (radio) {
        radio.checked = isCover
      }
    })
  }

  renderPreviews() {
    this.containerTarget.innerHTML = ""
    this.pendingReads = this.files.length
    this.toggleSubmit()
    this.updateAddButton()

    this.files.forEach(file => {
      const wrapper = document.createElement("div")
      wrapper.className = "relative group mb-[24px]"
      wrapper.dataset.fileId = file._id

      const skeleton = document.createElement("div")
      skeleton.className = "rounded shadow w-full bg-gray-100 flex flex-col items-center justify-center gap-2 animate-pulse"
      skeleton.style.minHeight = "200px"

      const spinner = document.createElement("div")
      spinner.className = "w-8 h-8 border-4 border-trust border-t-trust-deep rounded-full animate-spin"

      const label = document.createElement("p")
      label.className = "text-sm text-gray-500 mt-2"
      label.textContent = this._interpolate(this.loadingFileTextValue, { name: file.name })

      skeleton.appendChild(spinner)
      skeleton.appendChild(label)
      wrapper.appendChild(skeleton)
      this.containerTarget.appendChild(wrapper)

      const reader = new FileReader()

      reader.onload = e => {
        skeleton.remove()

        const imageContainer = document.createElement("div")
        imageContainer.className = "image-container relative"

        const img = document.createElement("img")
        img.src = e.target.result
        img.className =
          "rounded shadow object-cover w-full transition-transform duration-200"

        const removeBtn = document.createElement("button")
        removeBtn.type = "button"
        removeBtn.innerText = "✖"
        const isRtl = document.documentElement.dir === 'rtl'
        removeBtn.className =
          `absolute top-2 ${isRtl ? 'left-2' : 'right-2'} bg-error text-surface-100 text-xs px-2 py-1 rounded-control opacity-80`
        removeBtn.addEventListener("click", () => this.removeFile(file._id))

        imageContainer.appendChild(img)
        imageContainer.appendChild(removeBtn)

        if (!this.hasExistingCoverValue) {
          const isCover = file._id === this.selectedCoverFileId
          const coverBadge = document.createElement("span")
          coverBadge.className = `cover-badge absolute top-2 ${isRtl ? 'right-2' : 'left-2'} bg-green-600 text-white text-sm px-2 py-1 rounded-md shadow`;
          coverBadge.classList.toggle("hidden", !isCover);
          coverBadge.textContent = this.coverTextValue

          const coverRow = document.createElement("label")
          coverRow.className = "flex items-center gap-2 mt-2 cursor-pointer text-sm text-gray-700"
          const radio = document.createElement("input")
          radio.type = "radio"
          radio.name = "cover_selection"
          radio.className = "cover-radio accent-green-600 w-4 h-4"
          radio.checked = isCover
          radio.addEventListener("change", () => this.selectCover(file._id))
          coverRow.appendChild(radio)
          const radioLabel = document.createElement("span")
          radioLabel.textContent = this.useAsCoverImageTextValue
          coverRow.appendChild(radioLabel)

          imageContainer.appendChild(coverBadge)
          wrapper.appendChild(coverRow)
        }

        wrapper.prepend(imageContainer)

        this.pendingReads--
        this.toggleSubmit()
      }

      reader.onerror = () => {
        skeleton.remove()
        const errorMsg = document.createElement("p")
        errorMsg.className = "text-red-600 text-sm py-4 text-center"
        errorMsg.textContent = this._interpolate(this.failedToLoadFileTextValue, { name: file.name })
        wrapper.appendChild(errorMsg)

        this.pendingReads--
        this.toggleSubmit()
      }

      reader.readAsDataURL(file)
    })
  }

  toggleSubmit() {
    if (!this.hasSubmitTarget) return
    const loading = this.pendingReads > 0
    const uploading = this.activeUploads > 0

    if (uploading) {
      this.submitTarget.disabled = true
      this.submitTarget.classList.add("opacity-50", "cursor-not-allowed")
      this.submitTarget.value = this.uploadingImagesTextValue
    } else if (loading) {
      this.submitTarget.disabled = true
      this.submitTarget.classList.add("opacity-50", "cursor-not-allowed")
      this.submitTarget.value = this.loadingImagesTextValue
    } else {
      this.submitTarget.disabled = false
      this.submitTarget.classList.remove("opacity-50", "cursor-not-allowed")
      this.submitTarget.value = this.saveImagesTextValue
    }
  }

  updateInputFiles() {
    const dataTransfer = new DataTransfer()
    this.files.forEach(file => dataTransfer.items.add(file))
    this.inputTarget.files = dataTransfer.files
  }

  addError(message) {
    const errorEl = document.createElement("p")
    errorEl.className = "text-red-600 text-sm mt-2"
    errorEl.textContent = message
    this.errorsTarget.appendChild(errorEl)
  }

  clearErrors() {
    this.errorsTarget.innerHTML = ""
  }

  updateAddButton() {
    if (!this.hasAddPicturesButtonTarget) return
    const total = this.existingCountValue + this.files.length
    const isFull = total >= this.maxFilesValue

    if (isFull) {
      this.addPicturesButtonTarget.classList.add("opacity-50", "cursor-not-allowed", "border-trust-deep")
      this.addPicturesButtonTarget.style.pointerEvents = "none"
      if (!this.errorsTarget.querySelector(".max-limit-error")) {
        const p = document.createElement("p")
        p.className = "text-red-600 text-sm mt-2 max-limit-error"
        p.textContent = this._interpolate(this.maxLimitReachedTextValue, { count: this.maxFilesValue })
        this.errorsTarget.appendChild(p)
      }
    } else {
      this.addPicturesButtonTarget.classList.remove("opacity-50", "cursor-not-allowed", "border-trust-deep")
      this.addPicturesButtonTarget.style.pointerEvents = ""
      const error = this.errorsTarget.querySelector(".max-limit-error")
      if (error) error.remove()
    }
  }

  uploadInitialize(event) {
    const { id, file } = event.detail

    const originalFile = this.files.find(f => f.name === file.name && f.size === file.size)

    if (originalFile) {
      const wrapper = this.containerTarget.querySelector(`[data-file-id="${originalFile._id}"]`)
      if (wrapper) {
        wrapper.dataset.directUploadId = id

        const imageContainer = wrapper.querySelector(".image-container") || wrapper
        let progressContainer = imageContainer.querySelector(".upload-progress-container")
        if (!progressContainer) {
          progressContainer = document.createElement("div")
          progressContainer.className = "upload-progress-container absolute bottom-0 left-0 right-0 h-2 bg-gray-200 rounded-b opacity-90 overflow-hidden"

          const progressBar = document.createElement("div")
          progressBar.className = "upload-progress-bar h-full bg-blue-600 w-0 transition-all duration-300"
          progressContainer.appendChild(progressBar)

          imageContainer.appendChild(progressContainer)
        } else {
          progressContainer.classList.remove("hidden")
          const progressBar = progressContainer.querySelector(".upload-progress-bar")
          if (progressBar) progressBar.style.width = "0%"
        }
      }
    }
  }

  uploadStart(event) {
    this.activeUploads = (this.activeUploads || 0) + 1
    this.toggleSubmit()
  }

  uploadProgress(event) {
    const { id, progress } = event.detail
    const wrapper = this.containerTarget.querySelector(`[data-direct-upload-id="${id}"]`)
    if (wrapper) {
      const progressBar = wrapper.querySelector('.upload-progress-bar')
      if (progressBar) progressBar.style.width = `${progress}%`
    }
  }

  uploadEnd(event) {
    const { id } = event.detail
    const wrapper = this.containerTarget.querySelector(`[data-direct-upload-id="${id}"]`)
    if (wrapper) {
      const progressBar = wrapper.querySelector('.upload-progress-bar')
      if (progressBar) {
        progressBar.style.width = `100%`
        progressBar.classList.replace('bg-blue-600', 'bg-green-500')
      }
    }

    this.activeUploads = Math.max(0, (this.activeUploads || 0) - 1)
    this.toggleSubmit()
  }

  _interpolate(text, params) {
    return text.replace(/%\{(\w+)\}/g, (_, key) => params[key] != null ? params[key] : "")
  }
}